import { Outlet } from "react-router-dom";
import { Group, Panel, Separator } from "react-resizable-panels";
import { GripVertical, X } from "lucide-react";
import { useUIStore } from "@/stores/ui-store";
import { Sidebar } from "@/components/nav/Sidebar";
import { TopBar } from "@/components/nav/TopBar";
import { InspectorPanel } from "@/components/shared/InspectorPanel";
import { CommandPalette } from "@/components/shared/CommandPalette";
import { ToastContainer } from "@/components/shared/ToastContainer";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { useRealtimeNotifications } from "@/hooks/use-realtime-notifications";
import { useBrandingTheme } from "@/hooks/use-org-branding";

export function AppLayout() {
  const { inspectorOpen, closeInspector } = useUIStore();
  useRealtimeNotifications();
  useBrandingTheme();

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Fixed sidebar */}
      <Sidebar />

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar />

        <div className="flex-1 overflow-hidden">
          {inspectorOpen ? (
            <>
              {/* Desktop: resizable split panel */}
              <div className="hidden h-full md:block">
                <Group orientation="horizontal" className="h-full">
                  <Panel id="content" defaultSize={65} minSize={40}>
                    <main className="h-full overflow-y-auto p-6">
                      <ErrorBoundary>
                        <Outlet />
                      </ErrorBoundary>
                    </main>
                  </Panel>

                  <Separator className="flex w-2 items-center justify-center bg-border/50 transition-colors hover:bg-border">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </Separator>

                  <Panel id="inspector" defaultSize={35} minSize={20}>
                    <aside className="h-full overflow-y-auto border-l border-border bg-muted/30">
                      <InspectorPanel />
                    </aside>
                  </Panel>
                </Group>
              </div>

              {/* Mobile: main content + bottom sheet inspector */}
              <div className="h-full md:hidden">
                <main className="h-full overflow-y-auto p-4">
                  <ErrorBoundary>
                    <Outlet />
                  </ErrorBoundary>
                </main>

                {/* Bottom sheet overlay */}
                <div className="fixed inset-0 z-40">
                  <div
                    className="absolute inset-0 bg-black/30"
                    onClick={closeInspector}
                  />
                  <div className="absolute inset-x-0 bottom-0 h-[70vh] rounded-t-xl border-t border-border bg-card">
                    <div className="flex items-center justify-between border-b border-border px-4 py-2">
                      <span className="text-sm font-medium text-foreground">
                        Details
                      </span>
                      <button
                        onClick={closeInspector}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="h-[calc(70vh-3rem)] overflow-y-auto">
                      <InspectorPanel />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <main className="h-full overflow-y-auto p-4 md:p-6">
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </main>
          )}
        </div>
      </div>
      <CommandPalette />
      <ToastContainer />
    </div>
  );
}
